import {
  AllDataLayerActions,
  CartEventLocation,
  LoginAction,
  PageLoadAction,
  CategoryPageLoadAction,
  CheckoutPageLoadAction,
  ProductPageLoadAction,
  VariationPageLoadAction,
  OrderConfirmationPageLoadAction,
  RegisterAction,
  SelectCountryCodeAction,
  ClickInListProduct as ClickInListProductType,
  ClickInListProductAction,
  AddOrRemoveFromCartAction,
  CategoryTrackingInformationProduct,
  ViewItemListEventAction,
} from './data-layer-actions';
import { setLocalStorageItem, postJson, URLX, getLocalStorageItem } from '@polarnopyret/scope';
import TrackingInformationProductType from 'TrackingInformation/TrackingInformationProduct.type';

import State, { PageType } from 'Shared/State';
import isCheckoutPage from 'Checkout/Pages/Checkout/current-page-is-checkout';
import isVariationPage from 'Product/current-page-is-variation';
import isCategoryPage from 'Category/current-page-is-categorypage';
import isOrderConfirmationPage from 'Checkout/Pages/OrderConfirmation/current-page-is-order-confirmation';
import ProductListItemViewModel from '../Product/ProductListing/ProductListItemViewModel.type';
import Context from 'AppShell/Models/Context.type';

export function addToDataLayer(action: AllDataLayerActions) {
  const cb = (window as any).Cookiebot;
  if (cb) {
    // if cookiebot is present. make sure we are allowed to send data
    if (cb.hasResponse) {
      try {
        window.dataLayer.push(action);
      } catch (e) {
      }
      (window as any).dataLayerLastAction = null;
    } else {
      //log last action if the user accepts cookies
      if (action.event === "page_view") {
        (window as any).dataLayerLastAction = action;
      }
    }
  } else {
    try {
      window.dataLayer.push(action);
    } catch (e) {
    }
  }
}

export function initTagManager(context: Context) {
  if (!('dataLayer' in window)) {
    (window as any).dataLayer = [];
    (window as any).dataLayerLastAction = null;
    (window as any).dataLayer.push({ countryCode: context.twoLetterCountryCode });
  }
}

function getProductByCode(code: string): Promise<TrackingInformationProductType> {
  return postJson('/TrackingInformation/GetProductTrackingData', { code });
}

function getProductsByCodes(
  codes: Array<{ code: string; quantity: number }>,
): Promise<TrackingInformationProductType[]> {
  return postJson('/TrackingInformation/GetProductsTrackingData', { codes: codes.map(c => c.code) });
}

function productsToImpressions(
  products: ProductListItemViewModel[],
  from: number = 0,
  to: number = products.length,
) {
  const impressions: CategoryTrackingInformationProduct[] = [];

  for (let i = from; i < to; i++) {
    const p = products[i];
    impressions.push({
      item_name: p.displayName,
      item_id: formatTrackingId(p.itemNumber, p.code),
      price: p.price.sellingPrice ? p.price.sellingPrice : 0,
      item_brand: null,
      item_category: p.categoryName,
      item_variant: p.color,
      index: i + 1,
    } as CategoryTrackingInformationProduct);
  }
  return impressions;
}

function formatTrackingId(productItemNumber: String, variationCode: String) {
  if (!variationCode || !productItemNumber) {
    return productItemNumber;
  }

  const variationCodeParts = variationCode.split('_');
  return variationCodeParts.length === 2 ? productItemNumber + variationCodeParts[1] : productItemNumber;
}

export function addToCart(products: TrackingInformationProductType[], totalAmount: number, currency: string, location: CartEventLocation) {
  const action = {
    event: 'add_to_cart',
    eventInfo: {
      category: 'ecommerce interaction',
      action: 'addToCart'
    },
    ecommerce: {
      currency: currency,
      value: totalAmount,
      items: products.map(product => {
        const obj = Object.assign({}, product, {
          cartEventLocation: location,
        });
        return obj;
      }),
    },
  } as AddOrRemoveFromCartAction;

  addToCartThrottler(action);
}

let timeoutId: number = 0;
let cartActionsQueue: AddOrRemoveFromCartAction[] = [];
let trackingInformationProductsInQueue: Array<TrackingInformationProductType & {
  cartEventLocation: CartEventLocation;
}> = [];
function addToCartThrottler(action: AddOrRemoveFromCartAction) {
  cartActionsQueue.push(action);

  if (action.ecommerce && action.ecommerce.items.length > 0) {
    action.ecommerce.items.forEach(p => trackingInformationProductsInQueue.push(p));
  }

  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = 0;
  }
  timeoutId = setTimeout(excecuteCartThrottlerQueue, 2000);
}

function excecuteCartThrottlerQueue() {
  if (!cartActionsQueue.length) {
    return;
  }
  timeoutId = 0;

  const mergedActions: AddOrRemoveFromCartAction[] = mergeCartAddActions();
  mergedActions.forEach(mergedAction => addToDataLayer(mergedAction));
  trackingInformationProductsInQueue = [];
  cartActionsQueue = [];
}

function mergeCartAddActions(): AddOrRemoveFromCartAction[] {
  const intermediate = {};
  const currency = cartActionsQueue[0].ecommerce.currency;
  let addValue = 0;
  let removeValue = 0;

  cartActionsQueue.forEach(action => {
    if (action.ecommerce
      && action.ecommerce.items
      && action.event === 'add_to_cart') {
      addValue += action.ecommerce.value;
      action.ecommerce.items.forEach(product => {
        if (!(product.item_id in intermediate)) {
          intermediate[product.item_id] = product.quantity;
        } else {
          intermediate[product.item_id] += product.quantity;
        }
      });
    } else if (action.ecommerce
      && action.ecommerce.items
      && action.event === 'remove_from_cart') {
      removeValue += action.ecommerce.value;
      action.ecommerce.items.forEach(product => {
        if (!(product.item_id in intermediate)) {
          intermediate[product.item_id] = -product.quantity;
        } else {
          intermediate[product.item_id] -= product.quantity;
        }
      });
    }
  });

  const remove = {
    event: 'remove_from_cart',
    eventInfo: {
      category: 'ecommerce interaction',
      action: 'removeToCart'
    },
    ecommerce: {
      currency,
      value: removeValue,
      items: []
    }
  } as AddOrRemoveFromCartAction;

  const add = {
    event: 'add_to_cart',
    eventInfo: {
      category: 'ecommerce interaction',
      action: 'addToCart'
    },
    ecommerce: {
      currency,
      value: addValue,
      items: []
    }
  } as AddOrRemoveFromCartAction;

  for (const id in intermediate) {
    if (intermediate[id] > 0) {
      const addProduct = trackingInformationProductsInQueue.find(s => s.item_id === id);
      addProduct.quantity = intermediate[id];
      add.ecommerce.items.push(addProduct);
    } else if (intermediate[id] < 0) {
      const removeProduct = trackingInformationProductsInQueue.find(s => s.item_id === id);
      removeProduct.quantity = Math.abs(intermediate[id]);
      remove.ecommerce.items.push(removeProduct);
    }
  }

  const result: AddOrRemoveFromCartAction[] = [];
  if (remove.ecommerce.items.length > 0) {
    result.push(remove);
  }
  if (add.ecommerce.items.length > 0) {
    result.push(add);
  }
  return result;
}
export function removeFromCart(products: TrackingInformationProductType[],
    totalAmount: number,
    currency: string,
    location: CartEventLocation) {
  const action = {
    event: 'remove_from_cart',
    eventInfo: {
      category: 'ecommerce interaction',
      action: 'removeToCart'
    },
    ecommerce: {
        currency: currency,
        value: totalAmount,
        items: products.map(product => {
          const obj = Object.assign({}, product, {
            cartEventLocation: location,
          });
          return obj;
        }),
      },
  } as AddOrRemoveFromCartAction;

  addToCartThrottler(action);
}

export function login(method: 'Username/Password' | 'Facebook') {
  const action = {
    event: 'login',
    method: method
  } as LoginAction;
  addToDataLayer(action);
}

export function insightsPageView(page: PageType) {
  try {
    // Add page view to application insights tracking
    if ((window as any).appInsights) {
      const ai = (window as any).appInsights;
      ai.trackPageView();
    }
  }
  catch (error) {
  }
}

export function pageLoad(page: PageType, isDynamicPageLoad: boolean, state: State) {
  try {
    const searchParam = window.location.search
      .substr(1)
      .split('&')
      .find(q => q.substr(0, 2) === 'q=');
    const pathSufix = searchParam && searchParam.length > 0 && `?${searchParam}`;
    const path = page.url ? (pathSufix ? new URLX(page.url).pathname + pathSufix : new URLX(page.url).pathname) : '';
    let action = {
      event: 'page_view',
      pageData: {
        path,
        type: isDynamicPageLoad ? 'dynamic' : 'hard',
      },
      ecommerce: undefined,
    } as PageLoadAction;

    if (state.currentUser.trackingUser) {
      action.userData = {
        id: state.currentUser.trackingUser.id,
        registrationDate: state.currentUser.trackingUser.registrationDate,
      };
    }

    if (isCheckoutPage(page)) {
      addToDataLayer(Object.assign({}, action, {
        event: 'begin_checkout',
        eventInfo: {
          category: 'ecommerce interaction',
          action: 'checkout',
        },
        ecommerce: {
          currency: state.appShellData.market.currency,
          value: page.cart.totalPrice,
          items: page.cart?.trackingResult?.current ?? [],
          checkout: {
            actionField: {
              step: 1,
              emailAddress: page.customer.email,
              emailHashed: page.customer.emailHashed,
            },
          },
        },
      } as CheckoutPageLoadAction));
      
    } else if (isOrderConfirmationPage(page) && Boolean(page.trackingInformationTransaction)) {
      const ordersTracked = getLocalStorageItem<string[]>('tracking-information-transaction-notification-sent', []);
      const orderAlreadyTracked = Boolean(
        ordersTracked.find(s => s === page.trackingInformationTransaction.transactionId),
      );
      if (page.isFirstReceiptView && !orderAlreadyTracked) {
        let orderId = page.trackingInformationTransaction.transactionId;
        let orderAmount = page.trackingInformationTransaction.transactionTotalRevenue;
        let orderTaxAmount = page.trackingInformationTransaction.transactionTotalTax;
        let currency = page.trackingInformationTransaction.transactionCurrency;
        let shippingAmount = page.trackingInformationTransaction.transactionTotalShipping

        let orderAction = {
          event: 'purchase',
          eventInfo: {
            category: 'ecommerce interaction',
          },
          ecommerce: {
            currency: currency,
            value: Number.parseFloat(orderAmount),
            coupon: page.trackingInformationTransaction.transactionCouponCode,
            shipping: Number.parseFloat(shippingAmount),
            transaction_id: orderId,
            tax: Number.parseFloat(orderTaxAmount),         
            items: page.trackingInformationProducts,
          }
        } as OrderConfirmationPageLoadAction;

        addToDataLayer(Object.assign({}, action, orderAction));

        setLocalStorageItem('tracking-information-transaction-notification-sent', [
          ...ordersTracked,
          page.trackingInformationTransaction.transactionId,
        ]);
      }
    } else if (isVariationPage(page)) {
      const variationEventAction = {
        event: 'view_item',
      } as VariationPageLoadAction;

      addToDataLayer(action);

      const items: TrackingInformationProductType[] = [];
      items.push(page.trackingProduct);

      addToDataLayer(Object.assign({}, variationEventAction, {
        ecommerce: {
          currency: state.appShellData.market.currency,
          value: state.appShellData.cart.totalPrice,
          items
          },
        }) as ProductPageLoadAction);
    } else if (isCategoryPage(page)) {
      const perPage = 30;
      const numProducts = page.productList.products.length;

      let from = 0;
      if (isDynamicPageLoad) {
        from = Math.max(numProducts - perPage, 0);
      }

      const items = productsToImpressions(page.productList.products, from, numProducts);
      addToDataLayer(Object.assign({}, action, {
        ecommerce: {
          currency: state.appShellData.market.currency,
          value: state.appShellData.cart.totalPrice,
          item_list_name: page.pageName,
          items
        },
      }) as CategoryPageLoadAction);
    }

  } catch (error) {
    console.error(error);
  }
}

export function setCountryCode(countryCode: string) {
  const action = {
    event: 'set_user_properties',
    user_properties: {
      country_code: countryCode
    }
  } as SelectCountryCodeAction;

  addToDataLayer(action);
}

export function register() {
  const action = {
    event: 'sign_up',
  } as RegisterAction;
  addToDataLayer(action);
}

export function saveEmailToLocalStorage(email: string, emailHashed: string) {
  setLocalStorageItem('emailAddress', email);
  setLocalStorageItem('emailHashed', emailHashed);
}

export function productClickInList(product: ProductListItemViewModel, category: string, position: number, currency: string, value: number) {
  const items: ClickInListProductType[] = [
    {
      item_name: product.displayName,
      item_id: formatTrackingId(product.itemNumber, product.code),
      price: product.price.sellingPrice,
      item_brand: null,
      item_category: category,
      item_variant: product.color,
      index: position,
    } as ClickInListProductType,
  ];

  const action = {
    event: 'select_item',
    ecommerce: {
      currency,
      value,
      items
    },
  } as ClickInListProductAction;
  addToDataLayer(action);
}

export function genericImpressions(products: ProductListItemViewModel[], listName: string, currency: string, value: number) {
  const impressions = productsToImpressions(products);
  const action = {
    event: 'view_item_list',
    ecommerce: {
      currency,
      value,
      item_list_name: listName,
      items: impressions
    },
  } as ViewItemListEventAction;

  addToDataLayer(action);
}
